import { getCurrentPlatform } from '@/helpers/Platform/Platform';
import { botRegex } from '@/helpers/RUMService/botHelper';
import { TrackingConsent } from '@datadog/browser-core';
import { datadogRum, RumErrorEvent, RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { isAxiosError } from 'axios';
import isNil from 'lodash.isnil';

const ONE_TRUST_STRICT_CONSENT_GROUP = 'C0001';

export enum CustomAction {
  SubmitFunnel = 'Submit Funnel',
  VerifyOTP = 'Verify OTP',
}

export enum DDActionName {
  FunnelSubmitButton = 'Funnel Submit Button',
  VerifyOTPButton = 'Verify OTP Button',
  OfferTypeRadio = 'Offer Type Radio',
}

interface OneTrustCustomEvent extends CustomEvent {
  detail: string[];
}

interface ErrorLogContext {
  endpoint: string;
  requestData?: unknown;
  feature?: string; // Feature name if applicable
  [key: string]: unknown; // Other contextual information
}

const getTrackingConsent = (): TrackingConsent => {
  const consent = document.cookie.match(/(^| )OptanonConsent=([^;]+)/);
  const consentString = decodeURIComponent(consent ? consent[2] : '');

  return consentString.includes(`${ONE_TRUST_STRICT_CONSENT_GROUP}:1`)
    ? TrackingConsent.GRANTED
    : TrackingConsent.NOT_GRANTED;
};

// Uncaught "Object Not Found Matching Id:2, MethodName:update, ParamCount:4"
// https://github.com/DataDog/browser-sdk/issues/2715
const isUncaughtObjectNotFoundError = (error: RumErrorEvent['error']) => {
  const regex = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
  return regex.test(error.message);
};

// Uncaught {"type":"error","isTrusted":true,"currentTarget":null,"target":"[HTMLScriptElement]"}
const isUncaughtIsScriptLoadError = (error: RumErrorEvent['error']) => {
  const regex = /\{"type":"error","isTrusted":true,"currentTarget":null,"target":"\[.*?\]"\}/;
  return regex.test(error.message);
};

export const initDatadogRUM = (): void => {
  if (!['test', 'prod'].includes(import.meta.env.VUE_APP_STAGE)) return;

  const conditionalSampleRate = botRegex.test(navigator.userAgent) ? 0 : 20;
  const forceTracking = getTrackingFlag();

  datadogRum.init({
    applicationId: import.meta.env.VUE_DATADOG_RUM_APPLICATION_ID,
    clientToken: import.meta.env.VUE_DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: `${getCurrentPlatform()}-ubs-immocheck-fe`,
    env: import.meta.env.VUE_APP_STAGE,
    version: import.meta.env.VUE_COMMIT_SHA,
    sessionSampleRate:
      import.meta.env.VUE_APP_STAGE === 'prod' || forceTracking ? conditionalSampleRate : 0,
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask', // Mask the sensitive information, e.g. funnel inputs
    trackingConsent: getTrackingConsent(),
    beforeSend: (event: RumEvent, _context: RumEventDomainContext) => {
      if (event.type === 'error') {
        const error: RumErrorEvent['error'] = event.error;
        // Filter out strange Uncaught errors
        if (isUncaughtObjectNotFoundError(error) || isUncaughtIsScriptLoadError(error)) {
          return false;
        }

        // Filter out more errors
      }
      return true;
    },
  });

  // Set global context with platform
  datadogRum.setGlobalContext({
    platform: import.meta.env.VUE_APP_PLATFORM,
  });

  // Updates datadog tracking consent after user's interaction with OneTrust popup
  window.addEventListener('OneTrustGroupsUpdated', (event: Event) => {
    const customEvent = event as OneTrustCustomEvent;
    datadogRum.setTrackingConsent(
      customEvent.detail?.includes(ONE_TRUST_STRICT_CONSENT_GROUP)
        ? TrackingConsent.GRANTED
        : TrackingConsent.NOT_GRANTED,
    );
  });
};

export function getTrackingFlag(): boolean {
  return new URLSearchParams(window.location.search).get('trackSession') === 'true';
}

export const captureError = (error: Error, context?: object): void => {
  datadogRum.addError(error, context);
};

export const captureAction = (
  actionName: string,
  payload: Record<string, string | number>,
): void => {
  datadogRum.addAction(actionName, payload);
};

// Customize the axios error logging to include more information to help trace errors
export const logAxiosError = (error: unknown, context: ErrorLogContext): void => {
  if (!isAxiosError(error)) {
    datadogRum.addError(error as Error, {
      type: 'non-axios-error',
      ...context,
    });
    return;
  }

  const safePayload = obfuscateSensitiveData(context.requestData || error.config?.data);

  const fullContext = {
    ...context,
    requestData: safePayload,
    axios: {
      config: {
        url: error.config?.url,
        method: error.config?.method,
        params: error.config?.params,
      },
      ...(error.response && {
        response: {
          status: error.response.status,
          data: error.response.data,
        },
      }),
    },
  };

  const errorMessage = [
    `API Error: ${context.endpoint}`,
    error.response?.status && `Status: ${error.response.status}`,
    error.message,
  ]
    .filter(Boolean)
    .join(' | ');

  const loggedError = new Error(errorMessage);
  if (error.stack) loggedError.stack = error.stack;

  captureError(loggedError, fullContext);
};

const obfuscateSensitiveData = (data: unknown): unknown => {
  if (!data || typeof data !== 'object') return data;

  const sensitiveKeys = ['email', 'name', 'phoneNumber'];
  return Object.keys(data).reduce(
    (result, key) => {
      const value = data[key];
      if (!isNil(value) && sensitiveKeys.includes(key)) {
        result[key] = '*****';
      } else if (typeof value === 'object') {
        result[key] = obfuscateSensitiveData(value); // loop through nested objects
      } else {
        result[key] = value;
      }
      return result;
    },
    {} as Record<string, unknown>,
  );
};
